/**
 * NoteCard - Display component for a single note.
 */

import { Calendar, Edit05, Trash01 } from '@untitledui/icons';
import type { Membership } from 'api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import { formatCloseDate } from '@/lib/crm/pipeline/utils/deal-card-formatters';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { MemoAttributionRow } from './MemoAttributionRow';
import { MemoForm } from './MemoForm';
import { MemoFormControls } from './MemoFormControls';
import {
  containsHtmlTags,
  MAX_CHARS,
  MAX_LINES,
  NoteAttribution,
  NoteCardStageBadge,
  smartTruncateHtml,
  smartTruncatePlainText,
  useNoteAttributionData,
} from './NoteCard.subcomponents';
import { hasMeaningfulTitle } from './noteKind';
import {
  isOwnerOrCreator,
  type MemoEntityType,
  type MemoPrivacy,
  memoPrivacy,
} from './notes.utils';
import { preloadPlateNoteEditor } from './preloadPlateNoteEditor';
import { type Note } from './useMemoRecords';

interface NoteCardProps {
  /** Note to display */
  note: Note;
  /** Org ID — required so mention badges can hydrate via the CRM store */
  orgId: number;
  /**
   * Which note-like record this card shows. Drives the stage pill (which
   * pipeline family the stage resolves from and deep-links into) and the edit
   * form's stage picker. Defaults to `ahoy_note`; proposal cards and the task
   * surfaces pass `ahoy_task`.
   */
  entityType?: MemoEntityType;
  /** Display name of the note owner */
  ownerName?: string;
  /** Display name of the note creator (the user who originally created it). Optional; falls back to 'Unknown' if missing. */
  creatorName?: string;
  /** Whether the current user has permission to delete notes */
  canDelete?: boolean;
  /** Org members — passed through to the edit form's owner picker. */
  users?: Membership[];
  /** Current user's entity_id — fallback default owner in the edit form. */
  currentUserEntityId?: string;
  /** Called when edit is requested. When omitted, the edit button is hidden.
   *  Optional fields are bundled into `opts`. `stageAssociation: null` means
   *  the user explicitly cleared the stage; `undefined` means no change. */
  onEdit?: (
    noteEntityId: string,
    title: string,
    content: string,
    opts?: {
      date?: string;
      ownerEntityId?: string;
      /** null = explicitly removed; undefined = no change */
      stageAssociation?: string | null;
      /** undefined = no preference / no change */
      privacy?: MemoPrivacy;
    }
  ) => void;
  /**
   * Called when the edit button is clicked. When provided, opens the
   * external editor (e.g. compose panel) instead of the inline form.
   * Receives the note so the caller can pre-fill the editor.
   */
  onEditClick?: (note: Note) => void;
  /** Called when delete is requested — should return a Promise that rejects on failure.
   *  When omitted, the delete button is hidden. */
  onDelete?: (noteEntityId: string) => unknown;
  /** Whether edit is in progress */
  isEditing?: boolean;
  /** Whether delete is in progress */
  isDeleting?: boolean;
  /** Edit error */
  editError?: Error | null;
  /** Delete error */
  deleteError?: Error | null;
  /**
   * Whether to render the creator / owner / timestamp footer row. Defaults
   * to `true`. Set `false` from surfaces where this metadata doesn't apply
   * — e.g. proposal action cards that show a synthesized note preview
   * (no stored creator, owner, or timestamp). NoteAttribution also
   * auto-suppresses when every input is empty, so this is mainly an
   * explicit opt-out for callers that want the footer hidden even after
   * creator data starts flowing through.
   */
  showAttribution?: boolean;
  /**
   * Whether to render the bottom assignee footer row. Defaults to
   * `showAttribution`. The assignee chip itself is task-only — a note credits
   * its creator on the attribution line instead — except when a caller passes
   * `true` explicitly: proposal action cards do, with `showAttribution` false,
   * to surface the proposed owner without a created/edited line (a
   * synthesized, not-yet-created memo has no real timestamps). The footer
   * still auto-hides when there's nothing to show, so `true` is always safe.
   */
  showFooter?: boolean;
  /**
   * When provided, the stage pill becomes an interactive stage picker;
   * when omitted the pill stays read-only.
   */
  onStageChange?: (
    noteEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  /**
   * When true, render the full note body with NO truncation and NO
   * "Show more"/"Show less" toggle. Defaults to `false` so every existing
   * caller (notes tab in contact/company/deal slideouts, proposal action
   * cards, pipeline cards) keeps the 3-line clamp. Set `true` only from the
   * note record slideout (`NoteRecordOverview`), where the note IS the record.
   */
  disableTruncation?: boolean;
  /**
   * Record-slideout presentation. When true, drop the bordered "card" chrome
   * (border + background + rounded corners) and render the note flush on the
   * panel with a hairline divider beneath it, show the owner avatar inline in
   * the attribution line, and suppress the now-duplicate owner chip in the
   * footer. Defaults to `false` so every other surface (notes tab, proposal
   * action cards, pipeline cards) keeps the card treatment unchanged.
   */
  flat?: boolean;
  /**
   * When true, clicking the note title/body enters edit mode — on the record
   * slideout the note IS the record, so the whole body is the edit target.
   * Clicks on links and entity mentions still navigate. Defaults to `false`;
   * the "Edit note" button remains the keyboard / assistive-tech accessible
   * edit path on every surface (this is a mouse-only enhancement on top).
   */
  editOnClick?: boolean;
  /**
   * Controlled edit mode. When `onEditModeChange` is provided, the card no
   * longer owns its open/closed edit state — the parent does — and the inline
   * "Edit note" pencil is suppressed (the trigger lives elsewhere, e.g. the
   * slideout header for the note record surface). Leave both undefined on the
   * notes tab / proposal cards, where the card owns edit mode and renders its
   * own pencil.
   */
  isEditMode?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
}

/**
 * Component for displaying a single note with edit/delete actions.
 */
export function NoteCard({
  note,
  orgId,
  entityType = 'ahoy_note',
  ownerName,
  creatorName,
  canDelete = true,
  users,
  currentUserEntityId,
  onEdit,
  onEditClick,
  onDelete,
  isEditing = false,
  isDeleting = false,
  editError,
  deleteError,
  showAttribution = true,
  showFooter,
  onStageChange,
  disableTruncation = false,
  flat = false,
  editOnClick = false,
  isEditMode: controlledEditMode,
  onEditModeChange,
}: NoteCardProps) {
  const { timeFormat } = useOrgTimeFormat();
  // Edit mode is controllable: when `onEditModeChange` is passed the parent
  // owns the open/closed state (the note record slideout drives it from the
  // header "Edit note" button); otherwise the card owns it internally (notes
  // tab, proposal cards).
  const [internalEditMode, setInternalEditMode] = useState(false);
  const editModeControlled = onEditModeChange !== undefined;
  const isEditMode = editModeControlled
    ? (controlledEditMode ?? false)
    : internalEditMode;
  const setEditMode = useCallback(
    (next: boolean) => {
      if (editModeControlled) {
        onEditModeChange?.(next);
      } else {
        setInternalEditMode(next);
      }
    },
    [editModeControlled, onEditModeChange]
  );
  // Seamless in-place editing: on the note record slideout (flat + a parent
  // owning edit state) editing swaps ONLY the title + body for identically-
  // styled inputs, in place, keeping attribution + linked records visible —
  // instead of the full-form swap the notes tab / proposal cards use.
  const useInlineEdit = flat && editModeControlled;
  // The card is shared by the notes section and the tasks section; the action
  // labels name what the card actually is.
  const recordNoun = entityType === 'ahoy_task' ? 'task' : 'note';
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const prevIsEditingRef = useRef(isEditing);
  // Caret-at-click: viewport coords of a read-view BODY click, captured so the
  // editor drops the caret exactly there (see handleContentClick). Null for
  // title clicks / the header "Edit note" button (which focus the title).
  // Cleared whenever edit mode closes so a later header-open never reuses a
  // stale point.
  const editCaretPointRef = useRef<{ x: number; y: number } | null>(null);
  // Caret-at-click for the TITLE: the character offset under a read-<h4> click,
  // so the edit <input> drops the caret there instead of selecting all. Null
  // for body clicks and the header "Edit note" button. Cleared on edit close.
  const titleCaretOffsetRef = useRef<number | null>(null);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Close edit mode when save completes successfully (isEditing transitions
  // false -> no error). Skipped for the inline (record-slideout) variant, which
  // AUTOSAVES on every change: there each save flips isEditing, so exiting on
  // save-success would kick the user out mid-edit. Inline exit is driven solely
  // by MemoForm's Esc / click-away (commitAndExit -> onCancel -> the
  // handleEditCancel below -> setEditMode(false)).
  useEffect(() => {
    if (
      prevIsEditingRef.current &&
      !isEditing &&
      !editError &&
      isEditMode &&
      !useInlineEdit
    ) {
      setEditMode(false);
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, editError, isEditMode, setEditMode, useInlineEdit]);

  // Clear the captured caret point whenever edit mode closes, so a later open
  // via the header "Edit note" button (which sets no point) never reuses a
  // stale body-click position.
  useEffect(() => {
    if (!isEditMode) {
      editCaretPointRef.current = null;
      titleCaretOffsetRef.current = null;
    }
  }, [isEditMode]);

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick(note);
      return;
    }
    setEditMode(true);
  }, [onEditClick, note, setEditMode]);

  // Click-to-edit (record slideout only, gated by `editOnClick`). A mouse
  // enhancement layered on top of the "Edit note" button — clicks that land
  // on a link, a `<button>`, or an entity mention (rendered as a
  // role="button" chip) still navigate rather than opening the editor.
  const handleContentClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"]')) {
        return;
      }
      // Restore the caret at the click position. For a BODY click the Plate
      // editor resolves it from the coords; for a TITLE click we capture the
      // character offset under the pointer so the <input> drops the caret there
      // (the read <h4> and edit <input> share the same string, so it maps 1:1).
      const titleEl = target.closest('h4');
      if (titleEl) {
        editCaretPointRef.current = null;
        const caretPos = document.caretPositionFromPoint?.(
          e.clientX,
          e.clientY
        );
        titleCaretOffsetRef.current =
          caretPos && titleEl.contains(caretPos.offsetNode)
            ? caretPos.offset
            : null;
      } else {
        editCaretPointRef.current = { x: e.clientX, y: e.clientY };
        titleCaretOffsetRef.current = null;
      }
      handleEditClick();
    },
    [handleEditClick]
  );

  // Warm the lazy Plate.js editor chunk the instant the cursor arrives over
  // the note body, so a subsequent click into edit mode is instant even on
  // surfaces that don't preload on mount (the notes tab in other slideouts).
  // Idempotent — safe to fire on every hover.
  const handleContentPointerEnter = useCallback(() => {
    preloadPlateNoteEditor();
  }, []);

  const handleEditSubmit = useCallback(
    (
      title: string,
      content: string,
      date?: string,
      ownerEntityId?: string,
      stageAssociation?: string | null,
      privacy?: MemoPrivacy
    ) => {
      if (!onEdit) {
        return;
      }
      // Don't close form here - wait for save to complete via effect above.
      // Pass the form's stageAssociation through so callers can compute the
      // diff against `note.stage_entity_id` and emit the right add/remove
      // events. Same applies to `privacy`: NoteRecordOverview diffs it
      // against `note.is_private` and calls the togglePrivacy endpoint
      // when the values differ. Dropping either silently no-ops that
      // branch — see the "clear status" regression history.
      onEdit(note.entity_id, title, content, {
        date,
        ownerEntityId,
        stageAssociation,
        privacy,
      });
    },
    [onEdit, note.entity_id]
  );

  const handleEditCancel = useCallback(() => {
    setEditMode(false);
  }, [setEditMode]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete) {
      return;
    }
    try {
      await onDelete(note.entity_id);
      setShowDeleteConfirm(false);
    } catch {
      // deleteError is set by the parent — keep the dialog open so it renders
    }
  }, [onDelete, note.entity_id]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // Check if content needs truncation (use smart truncation to handle Entity tags)
  const { truncatedContent, needsTruncation } = useMemo(() => {
    if (disableTruncation) {
      return { truncatedContent: note.content, needsTruncation: false };
    }
    if (containsHtmlTags(note.content)) {
      const result = smartTruncateHtml(note.content, MAX_CHARS);
      return {
        truncatedContent: result.html,
        needsTruncation: result.truncated,
      };
    } else {
      const truncated = smartTruncatePlainText(
        note.content,
        MAX_CHARS,
        MAX_LINES
      );
      return {
        truncatedContent: truncated,
        needsTruncation: truncated !== note.content,
      };
    }
  }, [note.content, disableTruncation]);

  // Who the attribution line names, the compact/absolute times, and whether
  // the note was edited — see useNoteAttributionData for the derivation. A
  // note is authored, so it credits its creator (falling back to the owner
  // for legacy notes with no creator association). A task is assigned, not
  // authored: the assignee is the footer chip, and the line carries only
  // the time.
  const {
    wasEdited,
    compactTime,
    absoluteTime,
    attributionName,
    attributionInitials,
  } = useNoteAttributionData({
    note,
    entityType,
    creatorName,
    ownerName,
    timeFormat,
  });

  const ownerInitials = useMemo(
    () => (ownerName ? getAvatarInitials(ownerName) : undefined),
    [ownerName]
  );

  // Due date — tasks only. `ahoy_note` no longer carries ahoy_date/
  // ahoy_datetime (see #13931 memo-template removal), so a note never has a
  // due date to show; `ahoy_task` keeps both. Prefer `date`: for a timed task
  // the backend NoteDueDateCascade derives `ahoy_date` as the canonical
  // calendar day in the owner's timezone, so both are populated. formatCloseDate
  // reads only the YYYY-MM-DD prefix, so passing `datetime` would render its UTC
  // date — off by a day when the instant crosses a timezone boundary. Fall back
  // to `datetime` only when `date` is absent (the transient window before the
  // cascade's derived-date write arrives via websocket).
  const formattedDate = useMemo(() => {
    if (entityType !== 'ahoy_task') {
      return null;
    }
    const due = note.date || note.datetime;
    return due ? formatCloseDate(due) : null;
  }, [entityType, note.date, note.datetime]);

  // Record slideout (useInlineEdit) read view: resolve the owner's CRM object
  // from the store so the assignee avatar is ALWAYS visible at the end of the
  // attribution row — matching edit mode's MemoOwnerPicker avatar in the same
  // spot — rather than only appearing while editing. Only fetched on the
  // record slideout; other surfaces (notes tab) don't render it, so we pass
  // undefined to skip the fetch.
  const ownerObject = useCrmObjectSnapshot(
    useInlineEdit ? (note.owner_entity_id ?? undefined) : undefined,
    orgId
  );
  const readOwner = useMemo(
    () => ({ kind: 'avatar', object: ownerObject }) as const,
    [ownerObject]
  );

  // Record slideout read view: the privacy control at the end of the
  // attribution row is live, not just an indicator. A change is a privacy-only
  // edit: every other field is resubmitted unchanged, so the update mutation
  // diffs to nothing and only the privacy toggle fires (see
  // NoteRecordOverview.handleEdit). Owner/creator-only, like the edit form.
  const canToggleReadPrivacy =
    onEdit != null &&
    currentUserEntityId != null &&
    isOwnerOrCreator(note, currentUserEntityId);
  const handleReadPrivacyChange = useCallback(
    (isShared: boolean) => {
      if (!onEdit) {
        return;
      }
      onEdit(note.entity_id, note.title, note.content, {
        ownerEntityId: note.owner_entity_id ?? undefined,
        stageAssociation: note.stage_entity_id ?? null,
        privacy: isShared ? 'shared' : 'private',
      });
    },
    [onEdit, note]
  );

  const displayContent = isExpanded ? note.content : truncatedContent;

  // Edit/delete buttons render whenever the caller provides onEdit/onDelete
  // (and, for delete, canDelete is true). Callers control visibility by
  // withholding the callbacks — the notes list only renders notes the user
  // can read, and the backend permission rule now grants edit access to any
  // user who can view the note (owner, visible_user, or visible_to_org —
  // see apps/orgs/entities/permission_groups.yaml ahoy_default group).
  // The privacy/sharing toggle stays owner/creator-only (see MemoForm.tsx
  // canTogglePrivacy).

  // Edit mode (notes tab / proposal cards): swap to the full compact MemoForm.
  // The record slideout uses in-place editing instead (see `useInlineEdit`
  // below) — there editing must NOT replace the whole card, so it falls
  // through to the normal render where only the title + body become editable.
  if (isEditMode && !useInlineEdit) {
    return (
      <MemoForm
        mode='edit'
        note={note}
        entityType={entityType}
        users={users}
        currentUserEntityId={currentUserEntityId}
        orgId={orgId}
        onSubmit={handleEditSubmit}
        onCancel={handleEditCancel}
        isSubmitting={isEditing}
        error={editError}
      />
    );
  }

  // Delete confirmation
  if (showDeleteConfirm) {
    return (
      <div className='p-4 bg-primary border border-utility-error-200 rounded-xl'>
        <p className='text-sm text-primary mb-3'>
          Are you sure you want to delete this note? This action cannot be
          undone.
        </p>
        <div className='flex gap-2 justify-end'>
          <Button
            color='secondary'
            size='sm'
            onClick={handleDeleteCancel}
            isDisabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color='primary-destructive'
            size='sm'
            onClick={handleDeleteConfirm}
            isDisabled={isDeleting}
            isLoading={isDeleting}
          >
            Delete
          </Button>
        </div>
        {deleteError && (
          <p className='mt-2 text-xs text-utility-error-800'>
            We couldn&apos;t delete the note. Try again.
          </p>
        )}
      </div>
    );
  }

  // When users haven't typed a real title we skip the title row entirely and
  // let the body lead — a title that just says "Note" above a body adds
  // visual clutter without information. The rule (empty OR the legacy "Note"
  // placeholder = no title) lives in noteKind.ts so every surface agrees.
  const meaningfulTitle = hasMeaningfulTitle(note.title);

  // Compact attribution: creator (notes only) + time + "(edited)". Suppressed
  // entirely when `showAttribution` is false (e.g. proposal action cards) and
  // auto-suppressed when every input is empty. On the record slideout the
  // same element is rendered in read mode AND handed to the inline MemoForm
  // (`attribution` slot) in edit mode, so the row is identical on both sides
  // of the click-in; there the privacy state lives in the control at the end
  // of the row, so the inline lock cue is dropped.
  const attributionElement = showAttribution ? (
    <NoteAttribution
      name={attributionName}
      compactTime={compactTime}
      absoluteTime={absoluteTime}
      wasEdited={wasEdited}
      isPrivate={useInlineEdit ? false : note.is_private}
      // Flat (record slideout) shows the creator's avatar on the line. NOT on
      // the inline slideout, where the owner already has a real avatar at the
      // end of this same row (read and edit alike).
      avatarInitials={flat && !useInlineEdit ? attributionInitials : undefined}
    />
  ) : null;

  // Footer owner chip = the ASSIGNEE. Tasks always show it (a task is
  // assigned to someone). A note is authored, not assigned, so its card shows
  // no owner chip — its creator is already on the attribution line — unless
  // the caller explicitly opted into the footer (proposal action cards, which
  // surface the proposed owner alongside the due date).
  const footerEnabled = showFooter ?? showAttribution;
  const showOwnerChip =
    !!ownerName && !flat && (entityType === 'ahoy_task' || showFooter === true);

  return (
    <div
      className={cx(
        // `flat` (record slideout): the note IS the record, so drop the
        // card chrome and render flush on the panel with a hairline divider
        // beneath, matching the mockup. Every other surface keeps the card.
        flat
          ? 'pb-4 border-b border-[var(--color-border-secondary)] dark:border-white/15'
          : 'p-3 bg-primary border border-primary rounded-xl'
      )}
    >
      {/* Two-column layout. The right column stretches the full card
          height (`items-stretch`) so its `justify-between` can pin the
          edit/delete cluster to the top and the stage badge to the
          bottom, sharing a vertical axis — but the stage pill only
          renders when `entityType === 'ahoy_task'`, so for other entity
          types the column holds just the edit/delete cluster. */}
      <div className='flex items-stretch justify-between gap-2'>
        <div className='flex-1 min-w-0'>
          {/* Reserved edit-frame (record slideout only). The padding + rounded
              border geometry is IDENTICAL in read and edit — only the border
              COLOUR changes (transparent → visible) when editing. Because the
              box never changes size, the title/body don't shift a pixel when
              the border appears on click-in. Wraps the attribution too so the
              read view's left edge stays aligned with the body. */}
          <div
            className={cx(
              // `-mx-3` cancels the `p-3` horizontally so the title/body sit at
              // the box's natural left/right edge — flush with the hairline
              // divider below and the linked-records rows — while the edit-only
              // border sits 12px BEYOND the text on each side (the box grows
              // outward, the text stays put). Vertical `p-3` is kept so the
              // border also frames above/below; the geometry is identical in
              // read and edit (only the border colour changes), so entering edit
              // never shifts the text.
              useInlineEdit && '-mx-3 rounded-xl border p-3 transition-colors',
              useInlineEdit &&
                (isEditMode
                  ? 'border-[var(--color-border-secondary)]'
                  : 'border-transparent')
            )}
          >
            {/* Title + body wrapper. When `editOnClick` is set (record
              slideout), clicking anywhere in here enters edit mode — the
              handler ignores clicks on links / entity mentions so those
              still navigate. The "Edit note" button remains the keyboard /
              assistive-tech accessible edit path (this is a mouse-only
              enhancement on top). */}
            {isEditMode && useInlineEdit ? (
              /* Seamless in-place edit (record slideout): the full MemoForm,
               chromeless (`variant='inline'`), so the title + body stay
               pixel-identical and in place while ALL its controls (owner,
               privacy, date, stage, formatting toolbar, Save/Cancel) flow
               below the body. NoteCard's reserved box (added around this slot)
               supplies the edit-only border. */
              <MemoForm
                mode='edit'
                variant='inline'
                note={note}
                entityType={entityType}
                users={users}
                currentUserEntityId={currentUserEntityId}
                orgId={orgId}
                onSubmit={handleEditSubmit}
                onCancel={handleEditCancel}
                isSubmitting={isEditing}
                error={editError}
                initialCaretPoint={editCaretPointRef.current}
                initialTitleCaretOffset={titleCaretOffsetRef.current}
                attribution={attributionElement}
              />
            ) : (
              <>
                <div
                  className={cx(editOnClick && onEdit && 'cursor-text')}
                  onClick={
                    editOnClick && onEdit ? handleContentClick : undefined
                  }
                  onPointerEnter={
                    onEdit ? handleContentPointerEnter : undefined
                  }
                >
                  {/* Note title — only rendered when the user gave the note a
                    real title. Otherwise NOTHING renders above the body: it sits
                    flush at the top so the card reads as content-first, with no
                    empty title band (notes are created without titles now; a
                    reserved row was just dead space over the text).

                    Record slideout (useInlineEdit): a 24px title-only row
                    (`min-h-6`, 20px line) mirroring MemoFormTitleRow's inline
                    geometry exactly, so the body's top matches edit (no jump on
                    click-in). The owner avatar and privacy control live at the
                    end of the attribution row below, in read and edit alike. */}
                  {meaningfulTitle &&
                    (useInlineEdit ? (
                      <h4 className='min-h-6 text-sm font-semibold leading-5 text-primary'>
                        {note.title}
                      </h4>
                    ) : (
                      <h4 className='text-sm font-semibold text-primary mb-1'>
                        {note.title}
                      </h4>
                    ))}

                  {/* Note content — rendered by MarkdownContent so HTML,
                    formatting, and entity mentions all flow through the same
                    canonical pipeline (rehypeRaw + rehypeSanitize + entity
                    span renderer → MentionBadge). `[&_*]:text-inherit`
                    cascades the muted `text-secondary` colour through
                    CompactStrong/CompactEm/etc. which otherwise force
                    text-fg-primary. */}
                  <MarkdownContent
                    content={displayContent}
                    className='text-sm text-secondary break-words max-w-none [&_*]:text-inherit'
                    size='compact'
                    orgId={orgId}
                  />
                </div>

                {/* Show more/less toggle */}
                {needsTruncation && (
                  <button
                    type='button'
                    onClick={handleToggleExpanded}
                    className='text-sm text-fg-brand-primary hover:text-utility-brand-700 mt-1 cursor-pointer'
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </>
            )}

            {/* Attribution row (see `attributionElement`). While inline-editing
              MemoForm renders it instead (with its live controls), so it's
              skipped here. Record slideout read mode: the same
              MemoAttributionRow + MemoFormControls the editor uses — the read
              cluster just swaps the owner picker for a plain avatar — so the
              row is pixel-identical on both sides of the click-in. */}
            {attributionElement &&
              !(isEditMode && useInlineEdit) &&
              (useInlineEdit ? (
                <MemoAttributionRow
                  attribution={attributionElement}
                  controls={
                    <MemoFormControls
                      privacy={memoPrivacy(note)}
                      canTogglePrivacy={canToggleReadPrivacy}
                      onPrivacyChange={handleReadPrivacyChange}
                      submitLock={isEditing}
                      owner={readOwner}
                    />
                  }
                />
              ) : (
                attributionElement
              ))}
          </div>
        </div>

        {/* Right column: edit/delete buttons pinned to the top, stage
            badge pinned to the bottom. `flex-col justify-between` gives
            the visual top/bottom anchoring; `items-end` keeps both
            children right-aligned so they share the card's right edge.
            When there's no stage badge, `justify-between` still pins
            edit/delete to the top — the rendered output is identical to
            the previous single-cluster layout.

            The record slideout (useInlineEdit) omits this column entirely: its
            edit/delete live in the slideout header and its stage moved to the
            attribution row above, so the body claims the FULL width instead of
            wrapping short against a reserved side column. */}
        {!useInlineEdit && (
          <div className='flex flex-col items-end justify-between gap-2 shrink-0'>
            {/* Inline edit + delete buttons. Replaces the previous kebab
              dropdown — single click for either action, no extra menu
              hop. Sized at 24×24 with a 14px icon to match the
              MemoForm header buttons. */}
            <div className='flex items-center gap-0.5'>
              {/* Inline pencil — suppressed when edit mode is controlled by a
                parent (the note record slideout drives edit from the header
                "Edit note" button, so a second pencil here would be a
                duplicate). Still rendered on the notes tab / proposal cards,
                which own their edit state. */}
              {onEdit && !editModeControlled && (
                <Button
                  type='button'
                  iconLeading={Edit05}
                  color='tertiary'
                  size='sm'
                  className='!size-6 !p-1 [&_[data-icon]]:!size-3.5'
                  onClick={handleEditClick}
                  aria-label={`Edit ${recordNoun}`}
                  title={`Edit ${recordNoun}`}
                />
              )}
              {canDelete && onDelete && (
                <Button
                  type='button'
                  iconLeading={Trash01}
                  color='tertiary'
                  size='sm'
                  className='!size-6 !p-1 [&_[data-icon]]:!size-3.5 hover:!text-utility-error-600'
                  onClick={handleDeleteClick}
                  aria-label={`Delete ${recordNoun}`}
                  title={`Delete ${recordNoun}`}
                />
              )}
            </div>
            {/* Stage badge — tasks only, bottom-right corner, aligned with
              the edit/delete cluster above. Clicks deep-link to the
              pipeline board (PipelineBoard scrolls the matching column
              into view). NoteCardStageBadge is a no-op for notes, so it's
              always rendered rather than gated here. */}
            <NoteCardStageBadge
              orgId={orgId}
              entityType={entityType}
              stageEntityId={note.stage_entity_id}
              noteEntityId={note.entity_id}
              onStageChange={onStageChange}
            />
          </div>
        )}
      </div>
      {/* Footer: due date (tasks only — notes no longer carry a due date) +
          the assignee chip (see `showOwnerChip`). Flat mode never shows the
          chip — the person is already on the attribution line above. */}
      {footerEnabled && (formattedDate || showOwnerChip) && (
        <div
          className='mt-3 pt-2 px-0 border-t border-[var(--color-border-secondary)] dark:border-white/15 flex items-center justify-between gap-2 text-xs'
          data-testid='note-card-footer'
        >
          {formattedDate ? (
            <div className='flex items-center gap-1 text-[var(--color-text-tertiary)]'>
              <Calendar className='size-3 flex-shrink-0' />
              <span>{formattedDate}</span>
            </div>
          ) : (
            <span />
          )}
          {showOwnerChip ? (
            <div className='flex items-center gap-1.5 min-w-0 text-[var(--color-text-secondary)]'>
              <Avatar size='xxs' initials={ownerInitials} />
              <span className='truncate'>{ownerName}</span>
            </div>
          ) : (
            <span />
          )}
        </div>
      )}
    </div>
  );
}
